import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Heading from "ui/Text/Heading";
import ToggleTabs, { TabProps } from "ui/ToogleTabs";
import { spacing } from "globalStyles";
import { breakpoints } from "themes/breakpoints";
import Responsive from "ui/Responsive";
import { Icons } from "domain/public/Content/helper/icons";
import { AllInformationsSection } from "interfaces/content";
import { getComponentDynamically } from "../utils/content";

interface AllInformationsProps {
  data: AllInformationsSection["data"];
}

const AllInformations = ({ data }: AllInformationsProps) => {
  const [tabs, setTabs] = useState<Array<TabProps>>([]);

  useEffect(() => {
    const fetchTabs = async () => {
      if (!data?.tabs) return;

      const resolvedTabs = await Promise.all(
        data.tabs.map(async (tab) => {
          const tabItems = await Promise.all(
            tab.items.map(async (item) => {
              const ItemContent = await getComponentDynamically(item.content);

              return {
                id: item.id,
                label: item.label,
                content: ItemContent ? <ItemContent /> : null,
                approximateMaxHeight: item.approximateMaxHeight,
                hasVerticalScroll: item.hasVerticalScroll,
              };
            }),
          );

          return {
            label: tab.label,
            icon: Icons[tab.icon],
            items: tabItems,
            regulation: tab.regulation,
          };
        }),
      );

      setTabs(resolvedTabs);
    };

    fetchTabs();
  }, [data]);

  if (!tabs.length) return null;

  return (
    <Container id={data.id} className="anchor-offset">
      <div className="content-container">
        <Heading align="center" variant="h2" className="section-title" color="primary">
          {data.title}
        </Heading>
        <Responsive minWidth={breakpoints.sm}>
          <StyledToggleTabs tabs={tabs} />
        </Responsive>
        <Responsive maxWidth={breakpoints.sm} minWidth={breakpoints.xs}>
          <StyledToggleTabs tabs={tabs} buttonWidth={165} buttonHeight={40} />
        </Responsive>
        <Responsive maxWidth={breakpoints.xs}>
          <StyledToggleTabs tabs={tabs} buttonWidth={130} buttonHeight={40} />
        </Responsive>
      </div>
    </Container>
  );
};

export default AllInformations;

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.background.tertiary};
`;

const StyledToggleTabs = styled(ToggleTabs)`
  margin-top: ${spacing.large}px;

  .tab-content {
    margin-top: ${spacing.large}px;

    @media (max-width: ${breakpoints.md}) {
      margin-top: 20px;
    }
  }
`;
